<script setup>
import {Account} from '@/paks/mgr-models'
import {onMounted, reactive, ref} from 'vue'
import {Auth, Chat, Feedback, Log, Progress, State, can, clone, go} from '@/paks/vu-app'

const page = reactive({
    account: clone(State.auth.account),
    canCloseAccount: false,
    cloud: State.cache.clouds[0],
    closing: false,
    rules: [],
    saving: false,
})

//  Component refs
const confirm = ref(null)

onMounted(() => {
    if (can('owner') || (can('admin') && !State.config?.features?.auth?.owner)) {
        page.canCloseAccount = true
    }
})

async function save() {
    let account = await Account.updateIdentity({
        email: page.account.email.trim(),
        name: page.account.name.trim(),
    })
    if (account) {
        State.auth.setAccount(account)
    }
    Chat.identify()
    go('/')
}

async function closeAccount() {
    let confirmPrompt = `Delete ${page.account.name}`
    let approved = await confirm.value.ask(
        `<h2 class="closing">WARNING: Closing your account is permanent and cannot be reversed.</h2>
            <p>Closing your account will remove data for your devices.</p>
            <label>To confirm the deletion of your account, type <i>${confirmPrompt}</i> in the text box.</label>`,
        {color: 'error', confirmPrompt, width: 780}
    )
    if (!approved) {
        return
    }
    try {
        page.closing = true
        Progress.start('long')
        await State.app.closeAccount()
        Chat.cancelAccount()
        Feedback.info('Account Closed')
        await Auth.logout({redirect: true})
    } catch (err) {
        Log.error('Cannot close account', {err})
    } finally {
        Progress.stop()
        page.closing = false
    }
}
</script>

<template>
    <vu-form
        :data="page"
        :save="save"
        help="/doc/ui/account/settings.html"
        title="Account Settings"
        class="page-form ma-5 account-settings">
        <vu-input v-model="page.account.name" label="Account Name" type="text" cols="12" />
        <vu-input
            v-model="page.account.email"
            cols="12"
            label="Account Owner Email"
            type="text"
            :rules="page.rules.email" />
        <vu-input v-model="page.account.id" label="Account ID" :disabled="true" type="text" cols="12" />
        <vu-input v-model="page.cloud.id" label="Device Cloud ID" :disabled="true" type="text" cols="12" />

        <vu-input v-model="State.app.app.id" label="App ID" :disabled="true" type="text" cols="12" />

        <div class="actions">
            <v-btn size="small" color="primary" class="mr-2" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn size="small" color="none" @click="go('/')">Cancel</v-btn>
            <v-btn size="small" color="error" @click="closeAccount" :loading="page.closing" v-if="page.canCloseAccount">
                Close Account
            </v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>
</template>

<style lang="scss" scoped>
h2.closing {
    margin-bottom: 20px;
    color: #484848;
    font-weight: normal;
    font-size: 1.2rem;
}
.account-settings {
    max-width: 700px;
}
</style>
